import { GET, POST, PUT, DELETE } from '../dataSaver';
import Cookie from 'universal-cookie';
import jwt from 'jwt-decode';
import { PORTAL_TILE_UI, withQueryParams } from './portalTilesApi';
import { getProduct } from './demoWorkspaces';
import { toDemoSharePartnerPayload } from './partnersApi';
import { toDemoShareProspectPayload } from './prospectsApi';
import { toDemoShareOrganizationPayload } from './organizationsApi';

export const DEMO_SHARE_URL = 'application-management-service/demo-share';
export const DEMO_SHARE_CAPABILITIES_URL =
  'user-management-service/user/demo-share/capabilities';
export const DEMO_SHARE_STORIES_URL =
  'user-management-service/user/demo-share/stories';

/** Multipart JSON part key for create Demo Share. */
export const DEMO_SHARE_PAYLOAD_KEY = 'payload';

/** Multipart file part key for organization logo. */
export const DEMO_SHARE_LOGO_KEY = 'organizationLogo';

export const DEFAULT_ACCESS_DURATION_DAYS = 30;
export const DEFAULT_PROSPECT_ACCESS_DURATION_DAYS = 15;

export const ACCESS_CONFIG_TYPE = {
  DAYS: 'DAYS',
  END_DATE: 'END_DATE',
  FOREVER: 'FOREVER',
};

/** Predefined day durations only — do not add other day values. */
export const ACCESS_DURATION_DAY_OPTIONS = [15, 30, 60, 90];

export const ACCESS_LIMIT_OPTIONS = [
  { value: 'DAYS_15', label: '15 Days', type: ACCESS_CONFIG_TYPE.DAYS, durationDays: 15 },
  { value: 'DAYS_30', label: '30 Days', type: ACCESS_CONFIG_TYPE.DAYS, durationDays: 30 },
  { value: 'DAYS_60', label: '60 Days', type: ACCESS_CONFIG_TYPE.DAYS, durationDays: 60 },
  { value: 'DAYS_90', label: '90 Days', type: ACCESS_CONFIG_TYPE.DAYS, durationDays: 90 },
  { value: 'END_DATE', label: 'Specific End Date', type: ACCESS_CONFIG_TYPE.END_DATE },
  { value: 'FOREVER', label: 'Forever', type: ACCESS_CONFIG_TYPE.FOREVER },
];

export const DEFAULT_PROSPECT_ACCESS_LIMIT = 'DAYS_15';
export const DEFAULT_SALES_REP_ACCESS_LIMIT = 'DAYS_30';

/**
 * Which access-duration options are disabled in Create Demo Studio.
 * - Prospect audience: 90 days + Forever disabled (root /demo and sales-rep reshare)
 * - Sales-rep downstream share: Forever disabled unless the sharer has Forever access
 * - Sales-rep downstream share: day options that would end after the sharer's
 *   own access expiry are disabled
 */
export const getAccessLimitOptionDisabledFlags = ({
  audienceType,
  isChildShare = false,
  sharerAudienceType = '',
  parentAccessLimit = '',
  parentAccessEndDate = '',
  startDate = '',
} = {}) => {
  const selectedAudience = String(audienceType || '')
    .trim()
    .toUpperCase();
  const sharer = String(sharerAudienceType || '')
    .trim()
    .toUpperCase();
  const parentLimit = String(parentAccessLimit || '')
    .trim()
    .toUpperCase();
  const parentEnd = toAccessDateInputValue(parentAccessEndDate);
  const isProspectAudience = selectedAudience === AUDIENCE_TYPE.PROSPECT;
  const isProspectSharer =
    Boolean(isChildShare) && sharer === AUDIENCE_TYPE.PROSPECT;
  /** Sales-rep (or unknown) downstream shares: Forever only if parent has Forever. */
  const gateForeverOnParentAccess =
    Boolean(isChildShare) && !isProspectSharer;
  const parentHasForever = parentLimit === 'FOREVER';
  const rangeStart = toAccessDateInputValue(startDate) || getTodayAccessDateInput();

  return ACCESS_LIMIT_OPTIONS.map((option) => {
    if (option.value === 'DAYS_90' && isProspectAudience) return true;
    if (option.value === 'FOREVER') {
      if (isProspectAudience) return true;
      if (gateForeverOnParentAccess && !parentHasForever) return true;
    }
    // Cap day durations so sales-rep reshares cannot outlive their own grant.
    if (
      gateForeverOnParentAccess &&
      parentEnd &&
      option.type === ACCESS_CONFIG_TYPE.DAYS
    ) {
      const previewEnd = addDaysToDateInput(rangeStart, option.durationDays || 0);
      if (previewEnd && previewEnd > parentEnd) return true;
    }
    return false;
  });
};

/** Prefer a still-enabled option when the current selection becomes disabled. */
export const coerceAccessLimitToEnabledOption = (
  accessLimit,
  disabledFlags = []
) => {
  const current = String(accessLimit || '').trim();
  const currentIndex = ACCESS_LIMIT_OPTIONS.findIndex(
    (option) => option.value === current
  );
  if (currentIndex >= 0 && !disabledFlags[currentIndex]) {
    return current;
  }
  const fallback = ACCESS_LIMIT_OPTIONS.find(
    (option, index) => !disabledFlags[index]
  );
  return fallback?.value || DEFAULT_SALES_REP_ACCESS_LIMIT;
};

/** Today's date as YYYY-MM-DD (local). */
export const getTodayAccessDateInput = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** Human-readable access date, e.g. Sep 24, 2026. */
export const formatAccessDateLabel = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? new Date(`${raw}T12:00:00`)
    : new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const addDaysToDateInput = (dateInput, days) => {
  const base = String(dateInput || getTodayAccessDateInput()).trim();
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(base)
    ? new Date(`${base}T12:00:00`)
    : new Date(base);
  if (Number.isNaN(parsed.getTime())) return '';
  parsed.setDate(parsed.getDate() + Number(days || 0));
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Preview start/end labels for the access-duration UI.
 * @returns {{ startDate, endDate, startLabel, endLabel, summary }}
 */
export const getAccessDateRangePreview = ({
  accessLimit = 'DAYS_30',
  startDate,
  endDate,
} = {}) => {
  const start = String(startDate || getTodayAccessDateInput()).trim();
  const startLabel = formatAccessDateLabel(start) || start;
  const option =
    ACCESS_LIMIT_OPTIONS.find((item) => item.value === accessLimit) ||
    ACCESS_LIMIT_OPTIONS[0];

  if (option.type === ACCESS_CONFIG_TYPE.FOREVER) {
    return {
      startDate: start,
      endDate: '',
      startLabel,
      endLabel: 'Forever',
      summary: `${startLabel} – Forever`,
    };
  }

  if (option.type === ACCESS_CONFIG_TYPE.END_DATE) {
    const end = String(endDate || '').trim();
    const endLabel = end ? formatAccessDateLabel(end) : 'Not Specified';
    return {
      startDate: start,
      endDate: end,
      startLabel,
      endLabel,
      summary: `${startLabel} – ${endLabel}`,
    };
  }

  const computedEnd = addDaysToDateInput(start, option.durationDays || 30);
  const endLabel = formatAccessDateLabel(computedEnd) || computedEnd;
  return {
    startDate: start,
    endDate: computedEnd,
    startLabel,
    endLabel,
    summary: `${startLabel} – ${endLabel}`,
  };
};

export const AUDIENCE_TYPE = {
  SALES_REPRESENTATIVE: 'SALES_REPRESENTATIVE',
  PROSPECT: 'PROSPECT',
};

export const AUDIENCE_TYPE_OPTIONS = [
  {
    value: AUDIENCE_TYPE.SALES_REPRESENTATIVE,
    label: 'Sales Representative',
  },
  {
    value: AUDIENCE_TYPE.PROSPECT,
    label: 'Customer Prospect',
  },
];

export const STORY_SCOPE = {
  GENERAL: 'GENERAL',
  PROSPECT: 'PROSPECT',
};

export const STORY_SCOPE_OPTIONS = [
  {
    value: STORY_SCOPE.GENERAL,
    label: 'General Use',
    description: 'Available For Demo Studio Use Across Prospects.',
  },
  {
    value: STORY_SCOPE.PROSPECT,
    label: 'Specific Prospect',
    description: 'Prepared Specifically For The Selected Prospect.',
  },
];

/**
 * Prospect-scoped custom stories are only selectable when that prospect is
 * chosen (share step 2 / edit prospect). GENERAL (and unknown scope) always show.
 */
export const filterCustomStoriesForSelectedProspect = (
  stories = [],
  selectedProspectId = ''
) => {
  const selectedId = String(selectedProspectId || '').trim();
  return (stories || []).filter((story) => {
    if (!story) return false;
    const scope = String(story.storyScope || STORY_SCOPE.GENERAL)
      .trim()
      .toUpperCase();
    if (scope !== STORY_SCOPE.PROSPECT) return true;
    if (!selectedId) return false;
    const storyProspectId = String(
      story?.prospect?.prospectId || story?.prospectId || ''
    ).trim();
    return Boolean(storyProspectId) && storyProspectId === selectedId;
  });
};

export const SHARED_DEMO_SESSION_KEY = 'sharedDemoAccess';
export const SHARED_DEMO_SHARE_ID_KEY = 'sharedDemoShareId';
export const DEMO_CAPABILITIES_SESSION_KEY = 'demoShareCapabilities';

/**
 * Build accessConfig for create/update.
 * @param {{ accessLimit?: string, endDate?: string|Date, startDate?: string|Date }} opts
 */
export const buildAccessConfig = ({
  accessLimit = 'DAYS_30',
  endDate,
  startDate,
} = {}) => {
  const option =
    ACCESS_LIMIT_OPTIONS.find((item) => item.value === accessLimit) ||
    ACCESS_LIMIT_OPTIONS[0];
  const config = { type: option.type };

  if (option.type === ACCESS_CONFIG_TYPE.DAYS) {
    config.durationDays = option.durationDays;
  } else if (option.type === ACCESS_CONFIG_TYPE.END_DATE) {
    const iso = toAccessConfigIso(endDate);
    if (iso) config.endDate = iso;
  }

  const startIso = toAccessConfigIso(startDate);
  if (startIso) {
    config.startDate = startIso;
  }
  return config;
};

const toAccessConfigIso = (value) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  const raw = String(value).trim();
  if (!raw) return '';
  // date-only from <input type="date"> → end of day UTC for END_DATE clarity
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return `${raw}T23:59:59Z`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toISOString();
};

/** Validate accessConfig before submit. Returns { ok, message }. */
export const validateAccessConfigInput = ({
  accessLimit,
  endDate,
  startDate,
  maxEndDate,
} = {}) => {
  const option = ACCESS_LIMIT_OPTIONS.find((item) => item.value === accessLimit);
  if (!option) {
    return { ok: false, message: 'Choose an access duration.' };
  }
  if (option.type === ACCESS_CONFIG_TYPE.END_DATE) {
    if (!String(endDate || '').trim()) {
      return { ok: false, message: 'Choose a specific end date.' };
    }
    const end = new Date(
      /^\d{4}-\d{2}-\d{2}$/.test(String(endDate).trim())
        ? `${String(endDate).trim()}T23:59:59Z`
        : endDate
    );
    const start = startDate
      ? new Date(startDate)
      : new Date();
    if (Number.isNaN(end.getTime())) {
      return { ok: false, message: 'End date is invalid.' };
    }
    if (!(end.getTime() > start.getTime())) {
      return { ok: false, message: 'End date must be after the start date.' };
    }
  }

  const maxEnd = toAccessDateInputValue(maxEndDate);
  if (maxEnd) {
    if (option.type === ACCESS_CONFIG_TYPE.FOREVER) {
      return {
        ok: false,
        message: `Access cannot extend past your Demo Studio expiry (${formatAccessDateLabel(maxEnd) || maxEnd}).`,
      };
    }
    const preview = getAccessDateRangePreview({
      accessLimit,
      startDate: startDate || getTodayAccessDateInput(),
      endDate,
    });
    const effectiveEnd = toAccessDateInputValue(preview.endDate);
    if (effectiveEnd && effectiveEnd > maxEnd) {
      return {
        ok: false,
        message: `Access cannot extend past your Demo Studio expiry (${formatAccessDateLabel(maxEnd) || maxEnd}).`,
      };
    }
  }
  return { ok: true };
};

/** YYYY-MM-DD for access date fields from API ISO / date strings. */
export const toAccessDateInputValue = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  // Prefer the calendar date prefix so timezone offsets do not shift the day
  // (e.g. 2026-10-03T23:59:59Z → 2026-10-03, not the next local day).
  const calendarMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (calendarMatch) return calendarMatch[1];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** Parse YYYY-MM-DD (or Date-like) into a local Date for CommonDateField. */
export const accessDateInputToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? new Date(`${raw}T12:00:00`)
    : new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Format a Date (from CommonDateField) as YYYY-MM-DD in local time. */
export const dateToAccessDateInput = (date) => {
  if (!date) return '';
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Hydrate access-duration UI state from a Demo Share detail / list item.
 * Prefers accessConfig; falls back to accessType + accessDurationDays.
 */
export const resolveAccessLimitFromShare = (share = {}) => {
  const config = share?.accessConfig || {};
  const type = String(
    config.type || share.accessType || ACCESS_CONFIG_TYPE.DAYS
  )
    .trim()
    .toUpperCase();
  const durationDays = Number(
    config.durationDays ?? share.accessDurationDays ?? 0
  );
  const endDateRaw =
    config.endDate || share.externalAccessExpiryDate || share.endDate || '';
  const startDateRaw =
    config.startDate || share.accessStartDate || share.startDate || '';

  if (type === ACCESS_CONFIG_TYPE.FOREVER) {
    return {
      accessLimit: 'FOREVER',
      startDate: toAccessDateInputValue(startDateRaw) || getTodayAccessDateInput(),
      endDate: '',
    };
  }
  if (type === ACCESS_CONFIG_TYPE.END_DATE) {
    return {
      accessLimit: 'END_DATE',
      startDate: toAccessDateInputValue(startDateRaw) || getTodayAccessDateInput(),
      endDate: toAccessDateInputValue(endDateRaw),
    };
  }
  if (ACCESS_DURATION_DAY_OPTIONS.includes(durationDays)) {
    return {
      accessLimit: `DAYS_${durationDays}`,
      startDate: toAccessDateInputValue(startDateRaw) || getTodayAccessDateInput(),
      endDate: toAccessDateInputValue(endDateRaw),
    };
  }
  if (durationDays > 0) {
    const nearest = ACCESS_DURATION_DAY_OPTIONS.reduce((best, option) =>
      Math.abs(option - durationDays) < Math.abs(best - durationDays)
        ? option
        : best
    );
    return {
      accessLimit: `DAYS_${nearest}`,
      startDate: toAccessDateInputValue(startDateRaw) || getTodayAccessDateInput(),
      endDate: toAccessDateInputValue(endDateRaw),
    };
  }
  // Public/access APIs often expose only externalAccessExpiryDate (no accessConfig).
  if (endDateRaw) {
    return {
      accessLimit: 'END_DATE',
      startDate: toAccessDateInputValue(startDateRaw) || getTodayAccessDateInput(),
      endDate: toAccessDateInputValue(endDateRaw),
    };
  }
  return {
    accessLimit: 'DAYS_30',
    startDate: toAccessDateInputValue(startDateRaw) || getTodayAccessDateInput(),
    endDate: '',
  };
};

export const resolveCanShareDemoStudioFromShare = (share = {}) =>
  share?.permissions?.canShareDemoStudio === true ||
  share?.permissions?.canCreateDownstreamDemoStudio === true;

export const resolveCanManageDemoStudiosFromShare = (share = {}) =>
  share?.permissions?.canManageDemoStudios === true ||
  share?.permissions?.canManageSharedDemos === true ||
  share?.permissions?.canManageCreatedDemoStudios === true;

/** Normalize permissions for create/update Demo Share payloads. */
export const buildDemoSharePermissionsPayload = (permissions = {}) => {
  if (!permissions || typeof permissions !== 'object') return null;
  const next = {};
  if (Object.prototype.hasOwnProperty.call(permissions, 'canShareDemoStudio')) {
    next.canShareDemoStudio = permissions.canShareDemoStudio === true;
  }
  if (
    Object.prototype.hasOwnProperty.call(
      permissions,
      'canCreateDownstreamDemoStudio'
    )
  ) {
    next.canCreateDownstreamDemoStudio =
      permissions.canCreateDownstreamDemoStudio === true;
  }
  if (
    Object.prototype.hasOwnProperty.call(permissions, 'canManageDemoStudios')
  ) {
    next.canManageDemoStudios = permissions.canManageDemoStudios === true;
  }
  if (
    Object.prototype.hasOwnProperty.call(permissions, 'canManageSharedDemos')
  ) {
    next.canManageSharedDemos = permissions.canManageSharedDemos === true;
  }
  return Object.keys(next).length ? next : null;
};

/** Default permissions granted when creating a Sales Representative Demo Studio. */
export const DEFAULT_SALES_REP_PERMISSIONS = {
  canShareDemoStudio: true,
  canCreateDownstreamDemoStudio: true,
  canManageDemoStudios: true,
};

const normalizeProductSelection = (products = []) =>
  (products || [])
    .map((product) => {
      const workspaceKeys = Array.isArray(product?.workspaceKeys)
        ? product.workspaceKeys.filter(Boolean)
        : [];
      const customStoryIds = Array.isArray(product?.customStoryIds)
        ? product.customStoryIds.filter(Boolean)
        : [];
      if (!product?.tileKey || (!workspaceKeys.length && !customStoryIds.length)) {
        return null;
      }
      const next = {
        tileKey: product.tileKey,
        workspaceKeys: [...workspaceKeys],
      };
      if (customStoryIds.length) {
        next.customStoryIds = [...customStoryIds];
      }
      return next;
    })
    .filter(Boolean);

const normalizeExternalUsers = (externalUsers = [], { preserveUserId = false } = {}) =>
  (externalUsers || []).map((user) => {
    const countryCode = String(user.countryCode || user.mobileCountryCode || '')
      .trim();
    const base = {
      firstName: user.firstName?.trim() || '',
      lastName: user.lastName?.trim() || '',
      email: user.email?.trim() || '',
      mobileNumber: user.mobileNumber?.trim() || '',
      title: user.title?.trim() || '',
    };
    if (countryCode) {
      base.countryCode = countryCode.startsWith('+')
        ? countryCode
        : `+${countryCode.replace(/^\+/, '')}`;
    }
    if (preserveUserId && user.userId) {
      base.userId = user.userId;
    }
    return base;
  });

/** Optional mobile — accepts international numbers when a country dial code is set. */
export const validateDemoShareMobileNumber = (value, countryCode = '+1') => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return { ok: true };
  const dial = String(countryCode || '+1').trim() || '+1';
  const digits = trimmed.replace(/\D/g, '');
  const isNorthAmerica = dial === '+1';
  if (isNorthAmerica) {
    const formattedOk = /^\(\d{3}\) \d{3}-\d{4}$/.test(trimmed);
    if (formattedOk || digits.length === 10) {
      return { ok: true };
    }
    return {
      ok: false,
      message: 'Enter a valid 10-digit mobile number, or leave it blank.',
    };
  }
  if (digits.length >= 7 && digits.length <= 15) {
    return { ok: true };
  }
  return {
    ok: false,
    message: 'Enter a valid mobile number, or leave it blank.',
  };
};

/**
 * When shared access is within `warnWithinDays`, return a user-facing notice.
 * Forever / missing end dates return null.
 * Uses `externalAccessExpiryDate` from the public share API as the grant end.
 */
export const getDemoShareAccessExpiryNotice = (
  share = {},
  { warnWithinDays = 7 } = {}
) => {
  const explicitEndRaw = share?.externalAccessExpiryDate || '';

  if (!String(explicitEndRaw || '').trim()) {
    return null;
  }

  let endInput = toAccessDateInputValue(explicitEndRaw);

  if (!endInput) {
    return null;
  }

  const endParts = endInput.split('-').map((part) => Number(part));
  if (endParts.length !== 3 || endParts.some((n) => !Number.isFinite(n))) {
    return null;
  }
  const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
  endDate.setHours(23, 59, 59, 999);
  const now = new Date();
  if (endDate.getTime() < now.getTime()) return null;

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfEnd = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );
  const daysRemaining = Math.round(
    (startOfEnd.getTime() - startOfToday.getTime()) / 86400000
  );
  if (daysRemaining < 0 || daysRemaining > warnWithinDays) return null;

  let message = 'Your access to the Demo Studio expires today.';
  if (daysRemaining === 1) {
    message = 'Your access to the Demo Studio expires in 1 day.';
  } else if (daysRemaining > 1) {
    message = `Your access to the Demo Studio expires in ${daysRemaining} days.`;
  }

  return {
    daysRemaining,
    endDate: endInput,
    endLabel: formatAccessDateLabel(endInput) || endInput,
    message,
  };
};

/** Static / direct-launch experiences when workspace-tiles API has nothing. */
export const getFallbackShareWorkspaces = (tileKey) => {
  const key = (tileKey || '').toUpperCase();
  const productKey = PORTAL_TILE_UI[key]?.workspace || key;
  const product = getProduct(productKey);

  if (key === 'EDUSMART' || product?.externalLms) {
    return [
      {
        workspaceKey: 'EDUSMART_LEARNING',
        title: product?.lmsGuidance?.label || 'EDUSmart Learning',
        subtitle: 'Direct launch',
        description: product?.description || '',
        directLaunch: true,
        launchType: 'lms',
      },
    ];
  }

  if (key === 'INTEGRITY_SHIELD' || product?.externalIntegrityShield) {
    return (product?.workspaces || []).map((workspace) => ({
      workspaceKey: workspace.integrityModule || workspace.id,
      title: workspace.label,
      subtitle: workspace.role,
      description: workspace.description || '',
      guidance: workspace.guidance || workspace.description || '',
      integrityModule: workspace.integrityModule,
      directLaunch: true,
      launchType: 'integrity',
    }));
  }

  if (key === 'PROVIDER_PORTAL' || product?.skipRoleFilter) {
    return (product?.workspaces || []).map((workspace) => ({
      workspaceKey: workspace.id || workspace.role || 'PROVIDER_PORTAL',
      title: workspace.label,
      subtitle: workspace.role,
      description: workspace.description || '',
      guidance: workspace.guidance || workspace.description || '',
      navigationUrl: workspace.route || '/ApplicantDashboard',
      directLaunch: true,
      launchType: 'route',
    }));
  }

  if (key === 'TIMESMART' || product?.externalTimeSmart) {
    return (product?.workspaces || []).map((workspace) => ({
      workspaceKey: workspace.siteType || workspace.id,
      title: workspace.label,
      subtitle: workspace.role,
      description: workspace.description || '',
      guidance: workspace.guidance || workspace.description || '',
      siteType: workspace.siteType,
      directLaunch: true,
      launchType: 'timesmart',
    }));
  }

  return [];
};

export const buildDemoSharePayload = ({
  products = [],
  externalUsers = [],
  accessConfig,
  accessDurationDays,
  accessLimit,
  endDate,
  startDate,
  audienceType,
  parentShareId,
  partner,
  includePartner = false,
  organization,
  includeOrganization = false,
  prospect,
  includeProspect = false,
  permissions,
  includePermissions = false,
}) => {
  const payload = {
    products: normalizeProductSelection(products),
    externalUsers: normalizeExternalUsers(externalUsers),
  };

  // Prefer accessConfig object; fall back to legacy accessDurationDays only if needed.
  if (accessConfig && typeof accessConfig === 'object') {
    payload.accessConfig = accessConfig;
  } else if (accessLimit || endDate || startDate) {
    payload.accessConfig = buildAccessConfig({
      accessLimit: accessLimit || 'DAYS_30',
      endDate,
      startDate,
    });
  } else if (
    accessDurationDays !== undefined &&
    accessDurationDays !== null &&
    accessDurationDays !== ''
  ) {
    payload.accessConfig = {
      type: ACCESS_CONFIG_TYPE.DAYS,
      durationDays: Number(accessDurationDays) || DEFAULT_ACCESS_DURATION_DAYS,
    };
  } else {
    payload.accessConfig = buildAccessConfig({ accessLimit: 'DAYS_30' });
  }

  if (audienceType) {
    payload.audienceType = audienceType;
  }
  if (parentShareId) {
    payload.parentShareId = parentShareId;
  }
  // Root shares: send partner object or null. Child shares omit (backend inherits).
  if (includePartner && !parentShareId) {
    payload.partner = toDemoSharePartnerPayload(partner);
  }
  if (includeOrganization && !parentShareId) {
    const organizationPayload = toDemoShareOrganizationPayload(organization);
    if (organizationPayload) {
      payload.organization = organizationPayload;
    }
  }
  // Root shares: prospectId only. Child shares omit (backend inherits).
  if (includeProspect && !parentShareId) {
    const prospectPayload = toDemoShareProspectPayload(prospect);
    if (prospectPayload) {
      payload.prospect = prospectPayload;
    }
  }
  if (includePermissions && permissions && typeof permissions === 'object') {
    const permissionsPayload = buildDemoSharePermissionsPayload(permissions);
    if (permissionsPayload) {
      payload.permissions = permissionsPayload;
    }
  }
  return payload;
};

/**
 * Update payload for PUT /demo-share/{shareId}.
 * Preserves userId for existing recipients; new users omit it.
 * Recipients omitted from this list lose access.
 *
 * Partner/Prospect omitted → backend preserves existing values.
 */
export const buildDemoShareUpdatePayload = ({
  products = [],
  externalUsers = [],
  audienceType,
  partner,
  includePartner = false,
  organization,
  includeOrganization = false,
  prospect,
  includeProspect = false,
  accessConfig,
  accessLimit,
  endDate,
  startDate,
  permissions,
  includePermissions = false,
}) => {
  const payload = {
    products: normalizeProductSelection(products),
    externalUsers: normalizeExternalUsers(externalUsers, { preserveUserId: true }),
  };
  if (audienceType) {
    payload.audienceType = audienceType;
  }
  if (includePartner) {
    payload.partner = toDemoSharePartnerPayload(partner);
  }
  if (includeOrganization) {
    const organizationPayload = toDemoShareOrganizationPayload(organization);
    if (organizationPayload) {
      payload.organization = organizationPayload;
    }
  }
  if (includeProspect) {
    const prospectPayload = toDemoShareProspectPayload(prospect);
    if (prospectPayload) {
      payload.prospect = prospectPayload;
    }
  }
  if (accessConfig && typeof accessConfig === 'object') {
    payload.accessConfig = accessConfig;
  } else if (accessLimit || endDate || startDate) {
    payload.accessConfig = buildAccessConfig({
      accessLimit: accessLimit || 'DAYS_30',
      endDate,
      startDate,
    });
  }
  if (includePermissions && permissions && typeof permissions === 'object') {
    const permissionsPayload = buildDemoSharePermissionsPayload(permissions);
    if (permissionsPayload) {
      payload.permissions = permissionsPayload;
    }
  }
  return payload;
};

/**
 * Create Demo Share via multipart:
 * - payload: JSON string (audienceType, products, externalUsers, accessConfig, partner?, organization?, prospect?, permissions?, parentShareId?)
 * - organizationLogo: optional logo file (binary)
 */
export const createDemoShare = async ({
  products,
  externalUsers,
  accessConfig,
  accessDurationDays,
  accessLimit,
  endDate,
  startDate,
  audienceType,
  parentShareId,
  partner,
  includePartner = false,
  organization,
  includeOrganization = false,
  prospect,
  includeProspect = false,
  permissions,
  includePermissions = false,
  organizationLogoFile = null,
}) => {
  const payload = buildDemoSharePayload({
    products,
    externalUsers,
    accessConfig,
    accessDurationDays,
    accessLimit,
    endDate,
    startDate,
    audienceType,
    parentShareId,
    partner,
    includePartner,
    organization,
    includeOrganization,
    prospect,
    includeProspect,
    permissions,
    includePermissions,
  });
  const formData = new FormData();
  formData.append(DEMO_SHARE_PAYLOAD_KEY, JSON.stringify(payload));

  const logoFile =
    organizationLogoFile instanceof File
      ? organizationLogoFile
      : externalUsers.find((user) => user?.organizationLogoFile instanceof File)
          ?.organizationLogoFile;
  if (logoFile) {
    formData.append(DEMO_SHARE_LOGO_KEY, logoFile, logoFile.name);
  }

  const response = await POST(DEMO_SHARE_URL, formData);
  // App axios interceptor resolves HTTP errors instead of rejecting.
  const status = response?.response?.status ?? response?.status;
  if (response?.isAxiosError || (typeof status === 'number' && status >= 400)) {
    throw response;
  }
  return response;
};

export const sendDemoShareEmail = async (
  shareId,
  { to = [], cc = [], bodyPreview = '' } = {}
) => {
  const response = await POST(
    `${DEMO_SHARE_URL}/${encodeURIComponent(shareId)}/send-email`,
    {
      to,
      cc,
      bodyPreview,
    }
  );
  const status = response?.response?.status ?? response?.status;
  if (response?.isAxiosError || (typeof status === 'number' && status >= 400)) {
    throw response;
  }
  return response;
};

export const getDemoSharePublic = async (shareId, email) => {
  const query = email ? `?email=${encodeURIComponent(email)}` : '';
  return GET(
    `${DEMO_SHARE_URL}/shared/${encodeURIComponent(shareId)}/public${query}`
  );
};

export const getDemoShareAccess = async (shareId) => {
  return GET(`${DEMO_SHARE_URL}/shared/${encodeURIComponent(shareId)}/access`);
};

/** Normalize list responses from GET /demo-share. */
export const normalizeDemoShareList = (data) => {
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.shares)) return data.shares;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const DEFAULT_DEMO_SHARE_PAGE_SIZE = 20;

/** Normalize GET /demo-share?offset=&limit= into { items, total }. */
export const normalizeDemoShareListResponse = (data) => {
  const items = normalizeDemoShareList(data).map((share) => ({
    ...share,
    shareId: getShareId(share),
  }));
  const totalRaw = data?.total ?? data?.totalCount ?? data?.totalElements;
  const total = Number(totalRaw);
  return {
    items,
    total: Number.isFinite(total) && total >= 0 ? total : items.length,
  };
};

export const getShareId = (share) =>
  share?.shareId || share?.id || share?._id || '';

/** Recipients: prefer top-level externalUsers (current API), then nested. */
export const getShareExternalUsers = (share) => {
  if (Array.isArray(share?.externalUsers)) {
    return share.externalUsers;
  }
  if (Array.isArray(share?.recipients?.externalUsers)) {
    return share.recipients.externalUsers;
  }
  if (Array.isArray(share?.recipients) && share.recipients[0]?.email) {
    return share.recipients;
  }
  return [];
};

export const getShareProducts = (share) =>
  Array.isArray(share?.products) ? share.products : [];

export const getShareAccessLogs = (share) => {
  if (Array.isArray(share?.access?.accessLogs)) return share.access.accessLogs;
  if (Array.isArray(share?.accessLogs)) return share.accessLogs;
  return [];
};

/** Normalize a user's loginDetails; fall back to access.accessLogs when needed. */
export const resolveUserLoginDetails = (user, accessLogs = []) => {
  if (user?.loginDetails && typeof user.loginDetails === 'object') {
    const details = user.loginDetails;
    return {
      hasLoggedIn: Boolean(details.hasLoggedIn),
      loginCount: Number(details.loginCount) || 0,
      firstLoginDate: details.firstLoginDate || null,
      lastLoginDate: details.lastLoginDate || null,
      loginHistory: Array.isArray(details.loginHistory)
        ? details.loginHistory
        : [],
    };
  }

  const email = String(user?.email || '').trim().toLowerCase();
  const userId = String(user?.userId || '').trim();
  const matches = (accessLogs || []).filter((log) => {
    const logEmail = String(log?.email || '').trim().toLowerCase();
    const logUserId = String(log?.userId || '').trim();
    if (userId && logUserId && userId === logUserId) return true;
    if (email && logEmail && email === logEmail) return true;
    return false;
  });

  if (!matches.length) {
    return {
      hasLoggedIn: false,
      loginCount: 0,
      firstLoginDate: null,
      lastLoginDate: null,
      loginHistory: [],
    };
  }

  const sorted = [...matches].sort((a, b) => {
    const aTime = new Date(a?.date || 0).getTime();
    const bTime = new Date(b?.date || 0).getTime();
    return aTime - bTime;
  });

  return {
    hasLoggedIn: true,
    loginCount: sorted.length,
    firstLoginDate: sorted[0]?.date || null,
    lastLoginDate: sorted[sorted.length - 1]?.date || null,
    loginHistory: [...sorted]
      .reverse()
      .map((log) => ({
        date: log?.date || null,
        action: log?.action || 'LOGIN',
      })),
  };
};

/** @deprecated Use resolveUserLoginDetails */
export const buildLoginDetailsFromAccessLogs = resolveUserLoginDetails;

/** Aggregate access stats for the list Access column from externalUsers.loginDetails. */
export const getShareAccessSummary = (share) => {
  const users = getShareExternalUsers(share);
  const accessLogs = getShareAccessLogs(share);
  const detailsList = users.map((user) =>
    resolveUserLoginDetails(user, accessLogs)
  );
  const usageList = users.map((user) => resolveUserUsageDetails(user));

  const loggedInCount = detailsList.filter((details) => details.hasLoggedIn).length;
  const loginCount = detailsList.reduce(
    (sum, details) => sum + (details.loginCount || 0),
    0
  );
  const usersWithFeatureUse = usageList.filter(
    (details) => details.hasUsedFeatures
  ).length;
  const featureAccessCount = usageList.reduce(
    (sum, details) => sum + (details.totalAccessCount || 0),
    0
  );

  let latestLogin = null;
  detailsList.forEach((details) => {
    if (!details.lastLoginDate) return;
    const time = new Date(details.lastLoginDate).getTime();
    if (Number.isNaN(time)) return;
    if (!latestLogin || time > latestLogin) latestLogin = time;
  });

  // Fallback when only legacy accessLogs exist and users have no loginDetails.
  if (!latestLogin && accessLogs.length) {
    accessLogs.forEach((log) => {
      const time = new Date(log?.date || 0).getTime();
      if (Number.isNaN(time)) return;
      if (!latestLogin || time > latestLogin) latestLogin = time;
    });
  }

  let latestActivity = null;
  usageList.forEach((details) => {
    if (!details.lastActivityDate) return;
    const time = new Date(details.lastActivityDate).getTime();
    if (Number.isNaN(time)) return;
    if (!latestActivity || time > latestActivity) latestActivity = time;
  });

  return {
    recipientCount: users.length,
    loggedInCount:
      loggedInCount ||
      (accessLogs.length
        ? new Set(
            accessLogs
              .map((log) => String(log?.email || '').trim().toLowerCase())
              .filter(Boolean)
          ).size
        : 0),
    loginCount: loginCount || accessLogs.length,
    lastLoginDate: latestLogin ? new Date(latestLogin).toISOString() : null,
    usersWithFeatureUse,
    featureAccessCount,
    lastActivityDate: latestActivity
      ? new Date(latestActivity).toISOString()
      : null,
  };
};

export const getLatestAccessLogDate = (share) =>
  getShareAccessSummary(share).lastLoginDate;

export const DEMO_SHARE_USAGE_ACTION = {
  FEATURE_ACCESS: 'FEATURE_ACCESS',
  LINK_OPEN: 'LINK_OPEN',
  LINK_CLOSE: 'LINK_CLOSE',
};

const normalizeUsageLinks = (links) =>
  (Array.isArray(links) ? links : [])
    .map((link) => ({
      linkId: link?.linkId || '',
      linkType: link?.linkType || '',
      buttonText: link?.buttonText || '',
      openCount: Number(link?.openCount) || 0,
      completedCount: Number(link?.completedCount) || 0,
      totalDurationSeconds: Number(link?.totalDurationSeconds) || 0,
      averageDurationSeconds: Number(link?.averageDurationSeconds) || 0,
      firstOpenDate: link?.firstOpenDate || null,
      lastOpenDate: link?.lastOpenDate || null,
    }))
    .filter(
      (link) =>
        link.linkId ||
        link.openCount > 0 ||
        link.completedCount > 0 ||
        link.totalDurationSeconds > 0
    );

/** Normalize per-user feature usage tracking. */
export const resolveUserUsageDetails = (user) => {
  const details = user?.usageDetails;
  if (!details || typeof details !== 'object') {
    return {
      hasUsedFeatures: false,
      totalAccessCount: 0,
      totalLinkOpenCount: 0,
      completedLinkCount: 0,
      totalDurationSeconds: 0,
      averageDurationSeconds: 0,
      firstActivityDate: null,
      lastActivityDate: null,
      featureUsage: [],
    };
  }

  return {
    hasUsedFeatures: Boolean(details.hasUsedFeatures),
    totalAccessCount: Number(details.totalAccessCount) || 0,
    totalLinkOpenCount: Number(details.totalLinkOpenCount) || 0,
    completedLinkCount: Number(details.completedLinkCount) || 0,
    totalDurationSeconds: Number(details.totalDurationSeconds) || 0,
    averageDurationSeconds: Number(details.averageDurationSeconds) || 0,
    firstActivityDate: details.firstActivityDate || null,
    lastActivityDate: details.lastActivityDate || null,
    featureUsage: Array.isArray(details.featureUsage)
      ? details.featureUsage.map((item) => ({
          tileKey: item?.tileKey || '',
          workspaceKey: item?.workspaceKey || '',
          storyId: item?.storyId || null,
          description: item?.description || '',
          accessCount: Number(item?.accessCount) || 0,
          linkOpenCount: Number(item?.linkOpenCount) || 0,
          totalDurationSeconds: Number(item?.totalDurationSeconds) || 0,
          averageDurationSeconds: Number(item?.averageDurationSeconds) || 0,
          firstAccessDate: item?.firstAccessDate || null,
          lastAccessDate: item?.lastAccessDate || null,
          links: normalizeUsageLinks(item?.links),
        }))
      : [],
  };
};

/**
 * Record shared-demo recipient engagement.
 * Actions: FEATURE_ACCESS | LINK_OPEN | LINK_CLOSE
 * Master stories use workspaceKey; custom stories use storyId.
 */
export const recordDemoShareUsage = async (
  shareId,
  {
    action = DEMO_SHARE_USAGE_ACTION.FEATURE_ACCESS,
    tileKey,
    workspaceKey,
    storyId,
    linkId,
    engagementId,
    durationSeconds,
  } = {}
) => {
  if (!shareId || !tileKey) {
    return null;
  }

  const normalizedAction = String(action || DEMO_SHARE_USAGE_ACTION.FEATURE_ACCESS)
    .trim()
    .toUpperCase();
  const payload = {
    action: normalizedAction,
    tileKey,
  };

  if (normalizedAction === DEMO_SHARE_USAGE_ACTION.LINK_CLOSE) {
    if (!engagementId) return null;
    payload.engagementId = engagementId;
    payload.durationSeconds = Math.max(
      0,
      Math.round(Number(durationSeconds) || 0)
    );
  } else {
    if (storyId) {
      payload.storyId = storyId;
    } else if (workspaceKey) {
      payload.workspaceKey = workspaceKey;
    } else {
      return null;
    }

    if (normalizedAction === DEMO_SHARE_USAGE_ACTION.LINK_OPEN) {
      if (!linkId) return null;
      payload.linkId = linkId;
    }
  }

  const { data } = await POST(
    `${DEMO_SHARE_URL}/shared/${encodeURIComponent(shareId)}/usage`,
    payload
  );
  return data;
};

export const listDemoShares = async ({
  offset = 0,
  limit = DEFAULT_DEMO_SHARE_PAGE_SIZE,
} = {}) => {
  const safeOffset = Math.max(0, Number(offset) || 0);
  const safeLimit = Math.max(1, Number(limit) || DEFAULT_DEMO_SHARE_PAGE_SIZE);
  const { data } = await GET(
    withQueryParams(DEMO_SHARE_URL, {
      offset: safeOffset,
      limit: safeLimit,
    })
  );
  return normalizeDemoShareListResponse(data);
};

/** Sales Rep / Prospect requests more Demo Studio access time. */
export const requestDemoShareAccessExtension = async (
  shareId,
  { message = '' } = {}
) => {
  const id = String(shareId || '').trim();
  if (!id) throw new Error('Missing shareId');
  const { data } = await POST(
    `${DEMO_SHARE_URL}/shared/${encodeURIComponent(id)}/extension-request`,
    {
      message:
        String(message || '').trim() ||
        'Please extend my Demo Studio access.',
    }
  );
  return data;
};

/**
 * Creator extends Demo Studio access (1–365 additional days).
 * Updates Application Management + recipient demoAccessGrants.
 */
export const extendDemoShareAccess = async (
  shareId,
  { additionalDays } = {}
) => {
  const id = String(shareId || '').trim();
  if (!id) throw new Error('Missing shareId');
  const days = Math.round(Number(additionalDays));
  if (!Number.isFinite(days) || days < 1 || days > 365) {
    throw new Error('additionalDays must be between 1 and 365');
  }
  const { data } = await PUT(
    `${DEMO_SHARE_URL}/${encodeURIComponent(id)}/extend-access`,
    { additionalDays: days }
  );
  return data;
};

export const getDemoShareById = async (shareId) => {
  const response = await GET(
    `${DEMO_SHARE_URL}/${encodeURIComponent(shareId)}`
  );
  const status = response?.response?.status ?? response?.status;
  if (response?.isAxiosError || (typeof status === 'number' && status >= 400)) {
    throw response;
  }
  const data = response?.data ?? response;
  return {
    ...data,
    shareId: getShareId(data) || shareId,
  };
};

export const updateDemoShare = async (
  shareId,
  {
    products,
    externalUsers,
    audienceType,
    partner,
    includePartner = false,
    organization,
    includeOrganization = false,
    prospect,
    includeProspect = false,
    accessConfig,
    accessLimit,
    endDate,
    startDate,
    permissions,
    includePermissions = false,
  }
) => {
  const payload = buildDemoShareUpdatePayload({
    products,
    externalUsers,
    audienceType,
    partner,
    includePartner,
    organization,
    includeOrganization,
    prospect,
    includeProspect,
    accessConfig,
    accessLimit,
    endDate,
    startDate,
    permissions,
    includePermissions,
  });
  return PUT(`${DEMO_SHARE_URL}/${encodeURIComponent(shareId)}`, payload);
};

export const normalizeDemoShareCapabilities = (data = {}) => {
  const canCreateDownstreamDemoStudio = Boolean(
    data?.canCreateDownstreamDemoStudio
  );
  const canManageDemoStudios = Boolean(
    data?.canManageDemoStudios ??
      data?.canManageSharedDemos ??
      data?.canManageCreatedDemoStudios ??
      canCreateDownstreamDemoStudio
  );
  return {
    isDemoUser: Boolean(data?.isDemoUser),
    active: data?.active !== false,
    effectiveAudienceType: data?.effectiveAudienceType || null,
    canCreateStories: Boolean(data?.canCreateStories),
    canCreateDownstreamDemoStudio,
    canManageDemoStudios,
    raw: data || {},
  };
};

export const fetchDemoShareCapabilities = async ({
  persist = true,
  shareId,
} = {}) => {
  const { data } = await GET(
    withQueryParams(DEMO_SHARE_CAPABILITIES_URL, { shareId })
  );
  const normalized = normalizeDemoShareCapabilities(data);
  if (persist) {
    try {
      sessionStorage.setItem(
        DEMO_CAPABILITIES_SESSION_KEY,
        JSON.stringify(normalized)
      );
    } catch {
      // ignore storage errors
    }
  }
  return normalized;
};

export const readDemoShareCapabilities = () => {
  try {
    const raw = sessionStorage.getItem(DEMO_CAPABILITIES_SESSION_KEY);
    if (!raw) return null;
    return normalizeDemoShareCapabilities(JSON.parse(raw));
  } catch {
    return null;
  }
};

const normalizeCustomStoriesList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.stories)) return data.stories;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const normalizeCustomStory = (story = {}) => {
  const storyId = story?.storyId || story?.id || story?._id || '';
  const apiWorkspaceKey = String(story?.workspaceKey || '').trim();
  const storyScope = String(story?.storyScope || STORY_SCOPE.GENERAL)
    .trim()
    .toUpperCase();
  return {
    ...story,
    storyId,
    // Prefer backend-assigned key; synthetic fallback is client-only identity.
    workspaceKey: apiWorkspaceKey || (storyId ? `custom:${storyId}` : ''),
    parentTileKey: story?.parentTileKey || '',
    title: story?.title || 'Custom story',
    subtitle: story?.subtitle || 'Custom story',
    description: story?.description || '',
    guidanceDescription: story?.guidanceDescription || story?.guidance || '',
    ownerUserId: story?.ownerUserId || null,
    ownerUserName: story?.ownerUserName || null,
    storyScope:
      storyScope === STORY_SCOPE.PROSPECT
        ? STORY_SCOPE.PROSPECT
        : STORY_SCOPE.GENERAL,
    prospect: story?.prospect || null,
    isCustom: true,
    isCustomStory: true,
    active: story?.active !== false,
  };
};

/** Backend-assigned workspaceKey only (excludes client synthetic `custom:` keys). */
export const getPersistedWorkspaceKey = (item) => {
  const key = String(item?.workspaceKey || '').trim();
  if (!key || /^custom:/i.test(key)) return '';
  return key;
};

/** Current logged-in user id from JWT / session (for custom-story ownership checks). */
export const getCurrentDemoUserId = () => {
  try {
    const rawSession = sessionStorage.getItem('user');
    if (rawSession && rawSession !== 'undefined') {
      const parsed = JSON.parse(rawSession);
      if (parsed?.id) return String(parsed.id);
    }
  } catch {
    // fall through to cookie JWT
  }
  try {
    const cookie = new Cookie();
    const token = cookie.get('user');
    if (!token || token === 'undefined') return '';
    const decoded = jwt(token);
    return String(decoded?.id || '');
  } catch {
    return '';
  }
};

/** Current logged-in user email from session / JWT (for domain-locked resharing). */
export const getCurrentDemoUserEmail = () => {
  try {
    const rawSession = sessionStorage.getItem('user');
    if (rawSession && rawSession !== 'undefined') {
      const parsed = JSON.parse(rawSession);
      if (parsed?.email) return String(parsed.email).trim();
    }
  } catch {
    // fall through to cookie JWT
  }
  try {
    const cookie = new Cookie();
    const token = cookie.get('user');
    if (!token || token === 'undefined') return '';
    const decoded = jwt(token);
    return String(
      decoded?.email || decoded?.userEmail || decoded?.preferred_username || ''
    ).trim();
  } catch {
    return '';
  }
};

export const isCustomStoryOwnedByCurrentUser = (story, currentUserId = '') => {
  const ownerId = String(story?.ownerUserId || '').trim();
  const userId = String(currentUserId || getCurrentDemoUserId() || '').trim();
  if (!ownerId || !userId) return false;
  return ownerId === userId;
};

export const listDemoShareCustomStories = async (
  parentTileKey,
  { shareId } = {}
) => {
  const { data } = await GET(
    withQueryParams(DEMO_SHARE_STORIES_URL, {
      parentTileKey,
      shareId,
    })
  );
  return normalizeCustomStoriesList(data)
    .map(normalizeCustomStory)
    .filter((story) => story.storyId && story.active !== false);
};

export const createDemoShareCustomStory = async (
  payload,
  { shareId } = {}
) => {
  // shareId is required when the user has multiple demo accesses so the
  // backend can resolve the correct SALES_REPRESENTATIVE Demo Studio context.
  const response = await POST(
    withQueryParams(DEMO_SHARE_STORIES_URL, { shareId }),
    payload
  );
  // App axios interceptor resolves HTTP errors instead of rejecting.
  const status = response?.response?.status ?? response?.status;
  if (response?.isAxiosError || (typeof status === 'number' && status >= 400)) {
    throw response;
  }
  return normalizeCustomStory(response?.data);
};

export const updateDemoShareCustomStory = async (
  storyId,
  payload,
  { shareId } = {}
) => {
  const response = await PUT(
    withQueryParams(
      `${DEMO_SHARE_STORIES_URL}/${encodeURIComponent(storyId)}`,
      { shareId }
    ),
    payload
  );
  const status = response?.response?.status ?? response?.status;
  if (response?.isAxiosError || (typeof status === 'number' && status >= 400)) {
    throw response;
  }
  return normalizeCustomStory(response?.data);
};

export const deleteDemoShareCustomStory = async (storyId, { shareId } = {}) => {
  const response = await DELETE(
    withQueryParams(
      `${DEMO_SHARE_STORIES_URL}/${encodeURIComponent(storyId)}`,
      { shareId }
    )
  );
  const status = response?.response?.status ?? response?.status;
  if (response?.isAxiosError || (typeof status === 'number' && status >= 400)) {
    throw response;
  }
  return response;
};

export const fetchDemoShareTree = async (shareId) => {
  const { data } = await GET(
    `${DEMO_SHARE_URL}/${encodeURIComponent(shareId)}/tree`
  );
  return data;
};

export const persistSharedDemoAccess = (shareId, accessPayload) => {
  sessionStorage.setItem(SHARED_DEMO_SHARE_ID_KEY, shareId || '');
  sessionStorage.setItem(
    SHARED_DEMO_SESSION_KEY,
    JSON.stringify(accessPayload || {})
  );
};

export const readSharedDemoAccess = () => {
  try {
    const raw = sessionStorage.getItem(SHARED_DEMO_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearSharedDemoAccess = () => {
  sessionStorage.removeItem(SHARED_DEMO_SESSION_KEY);
  sessionStorage.removeItem(SHARED_DEMO_SHARE_ID_KEY);
};

export const getShareIdFromSession = () =>
  sessionStorage.getItem(SHARED_DEMO_SHARE_ID_KEY) || '';
